import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CalendarCheck, Warning, Clock, Phone } from "@phosphor-icons/react";

const TYPE_ROUTE = {
  "ride-requests": "/admin/ride-requests", "quotes": "/admin/quotes",
  "facility-leads": "/admin/facility-leads", "contacts": "/admin/contacts",
  "applications": "/admin/applications",
};
const PRIORITY = {
  Urgent: "bg-rose-100 text-rose-700", Important: "bg-amber-100 text-amber-700",
  Normal: "bg-slate-100 text-slate-600",
};

function Card({ item }) {
  const navigate = useNavigate();
  const when = item.follow_up_date + (item.follow_up_time ? ` ${item.follow_up_time}` : "");
  return (
    <div data-testid={`followup-${item.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-heading text-base font-semibold text-slate-900">{item.name || "—"}</p>
          <p className="text-xs text-slate-500">{item.type_label}</p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${PRIORITY[item.priority] || PRIORITY.Normal}`}>{item.priority}</span>
      </div>
      <div className="mt-3 space-y-1 text-sm text-slate-600">
        <p className="flex items-center gap-1.5"><Clock size={15} weight="fill" className="text-slate-400" /> {when}</p>
        <p>Status: <span className="font-medium text-slate-800">{item.status}</span></p>
        <p>Assigned: <span className="font-medium text-slate-800">{item.assigned_to || "Unassigned"}</span></p>
        {item.phone && <p className="flex items-center gap-1.5"><Phone size={14} weight="fill" className="text-slate-400" /> {item.phone}</p>}
        {item.notes && <p className="mt-1 rounded-lg bg-slate-50 p-2 text-xs text-slate-600">{item.notes}</p>}
      </div>
      <button onClick={() => navigate(TYPE_ROUTE[item.type])} className="mt-3 text-sm font-semibold text-brand-purple hover:underline">
        Open in {item.type_label} →
      </button>
    </div>
  );
}

function Section({ title, icon: Icon, tone, items, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={20} weight="fill" className={tone} />
        <h2 className="font-heading text-lg font-bold text-slate-900">{title}</h2>
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-600">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-slate-400">Nothing here.</p>
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{items.map((i) => <Card key={i.type + i.id} item={i} />)}</div>
      )}
    </div>
  );
}

export default function FollowUps() {
  const [data, setData] = useState({ overdue: [], due_today: [], upcoming: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/follow-ups").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="admin-followups" className="space-y-10">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Follow-Ups Due</h1>
        <p className="mt-1 text-sm text-slate-500">Leads with a scheduled follow-up. Completed and closed leads are hidden.</p>
      </div>
      {loading ? <p className="text-slate-400">Loading…</p> : (
        <>
          <Section title="Overdue" icon={Warning} tone="text-rose-600" items={data.overdue} testid="followups-overdue" />
          <Section title="Due Today" icon={CalendarCheck} tone="text-brand-purple" items={data.due_today} testid="followups-today" />
          <Section title="Upcoming" icon={Clock} tone="text-brand-teal" items={data.upcoming} testid="followups-upcoming" />
        </>
      )}
    </div>
  );
}
